import { NowAndNext, NowAndNextData } from '../../types/Asset';
import { buildNowAndNextItem } from '../../utils/fnParser';

/**
 * NowAndNext:
 * Rolls the current programs over when they end, using cached schedule data, so the programs update
 * without waiting for the next API refresh cycle.
 */

const CHECK_INTERVAL_MS = 5_000;
const REFRESH_COOLDOWN_MS = 60_000;

export class ProgramScheduleManager {
    private intervalId: number | null = null;

    private data: NowAndNextData | null = null;

    private lastRefreshRequest = 0;

    private onProgramUpdate: (updatedPrograms: NowAndNext[]) => void;

    private onRefreshNeeded?: () => void;

    constructor(onProgramUpdate: (updatedPrograms: NowAndNext[]) => void, onRefreshNeeded?: () => void) {
        this.onProgramUpdate = onProgramUpdate;
        this.onRefreshNeeded = onRefreshNeeded;
    }

    setData(data: NowAndNextData): void {
        this.data = data;
    }

    start(): void {
        if (this.intervalId) return;

        this.intervalId = window.setInterval(() => this.checkPrograms(), CHECK_INTERVAL_MS);
    }

    stop(): void {
        if (!this.intervalId) return;

        window.clearInterval(this.intervalId);
        this.intervalId = null;
    }

    private checkPrograms(): void {
        if (!this.data) return;

        const now = Date.now();
        const { currentPrograms } = this.data;

        if (!currentPrograms.some((program) => now >= program.endTime * 1000)) return;

        const updatedPrograms = this.updateCurrentPrograms(this.data, now);
        const hasChanges = updatedPrograms.some((program, index) => program.id !== currentPrograms[index].id);

        if (hasChanges) {
            this.data = { ...this.data, currentPrograms: updatedPrograms };
            this.onProgramUpdate(updatedPrograms);
            return;
        }

        // A program ended but the cached schedule holds no replacement for it
        if (now - this.lastRefreshRequest < REFRESH_COOLDOWN_MS) return;

        this.lastRefreshRequest = now;
        this.onRefreshNeeded?.();
    }

    private updateCurrentPrograms(data: NowAndNextData, now: number): NowAndNext[] {
        return data.currentPrograms.map((currentProgram) => {
            if (now < currentProgram.endTime * 1000) return currentProgram;

            const channelPrograms = data.channelPrograms[currentProgram.channelId];
            if (!channelPrograms?.length) return currentProgram;

            const nowProgram = channelPrograms.find((prog) => {
                const startMs = prog.start * 1000;
                const endMs = (prog.start + prog.duration) * 1000;
                return startMs <= now && now < endMs;
            });

            if (!nowProgram) return currentProgram;

            const futurePrograms = channelPrograms.filter((prog) => prog.start > nowProgram.start).sort((a, b) => a.start - b.start);

            return {
                ...buildNowAndNextItem(nowProgram, futurePrograms[0], undefined, currentProgram.next_request_in),
                channelLogo: currentProgram.channelLogo,
                channelNumber: currentProgram.channelNumber,
                isChannelSubscribed: currentProgram.isChannelSubscribed,
                subscribeOffer: currentProgram.subscribeOffer ?? null,
            };
        });
    }
}
